// 為替レート表（§4）。poe2scout のカレンシー一覧から rate(X)=通貨 X 1個の base 換算価値を構築。
// base=Exalted は定義上 rate=1。未取得通貨は undefined（呼び出し側で警告＆除外, 黙って0にしない）。
import { BASE_CURRENCY, DISPLAY_UNITS, MARKET_CATEGORIES } from '../config';
import type { CatalogItem, Poe2ScoutCurrency } from '../api/types';

export interface RateEntry {
  id: string;
  rate: number; // base 換算（exalted 建て）
  text: string;
  iconUrl?: string;
  category?: string;
  categoryLabel?: string;
}

export class RateTable {
  private readonly map: Map<string, RateEntry>;
  readonly league: string;
  readonly fetchedAt: number; // ms epoch（鮮度表示用）

  private constructor(map: Map<string, RateEntry>, league: string, fetchedAt: number) {
    this.map = map;
    this.league = league;
    this.fetchedAt = fetchedAt;
  }

  static build(currencies: Poe2ScoutCurrency[], league: string, fetchedAt: number): RateTable {
    const map = new Map<string, RateEntry>();
    for (const c of currencies) {
      // UTC 日替わり直後は最新ログが {Price: null} のことがある（2026-07-11 実測）→ 最初の有効価格まで遡る。
      const price = c.PriceLogs?.find((p) => p?.Price != null)?.Price;
      if (typeof price === 'number' && price > 0) {
        map.set(c.ApiId, { id: c.ApiId, rate: price, text: c.Text, iconUrl: c.IconUrl });
      }
    }
    // base 通貨は必ず rate=1（API の値に依らず固定）。
    const baseExisting = map.get(BASE_CURRENCY);
    map.set(BASE_CURRENCY, {
      id: BASE_CURRENCY,
      rate: 1,
      text: baseExisting?.text ?? 'Exalted Orb',
      iconUrl: baseExisting?.iconUrl,
    });
    return new RateTable(map, league, fetchedAt);
  }

  /** market カタログ（全カテゴリ）からレート表を構築。base=exalted は固定1。 */
  static buildCatalog(catalog: CatalogItem[], league: string, fetchedAt: number): RateTable {
    const map = new Map<string, RateEntry>();
    for (const { item, category, categoryLabel } of catalog) {
      if (map.has(item.ApiId)) continue; // 重複は先勝ち
      const price = item.PriceLogs?.find((p) => p?.Price != null)?.Price;
      if (typeof price === 'number' && price > 0) {
        map.set(item.ApiId, { id: item.ApiId, rate: price, text: item.Text, iconUrl: item.IconUrl, category, categoryLabel });
      }
    }
    const ex = map.get(BASE_CURRENCY);
    map.set(BASE_CURRENCY, {
      id: BASE_CURRENCY,
      rate: 1,
      text: ex?.text ?? 'Exalted Orb',
      iconUrl: ex?.iconUrl,
      category: 'currency',
      categoryLabel: 'Currency',
    });
    return new RateTable(map, league, fetchedAt);
  }

  /** テスト用: 素の {id:rate} から構築。 */
  static fromRates(rates: Record<string, number>, league = 'test', fetchedAt = 0): RateTable {
    const map = new Map<string, RateEntry>();
    for (const [id, rate] of Object.entries(rates)) {
      map.set(id, { id, rate, text: id });
    }
    map.set(BASE_CURRENCY, { id: BASE_CURRENCY, rate: 1, text: 'Exalted Orb' });
    return new RateTable(map, league, fetchedAt);
  }

  /** 通貨 X 1個の base 換算価値。未取得は undefined。 */
  rate(id: string): number | undefined {
    return this.map.get(id)?.rate;
  }

  has(id: string): boolean {
    const r = this.map.get(id)?.rate;
    return r != null && r > 0;
  }

  /** 単位セレクト・表示用の一覧（rate 降順）。 */
  entries(): RateEntry[] {
    return [...this.map.values()].sort((a, b) => b.rate - a.rate);
  }

  /** カタログ全品（datalist 用のフラット一覧, rate 降順）。 */
  catalogList(): RateEntry[] {
    return this.entries();
  }

  /** 表示名（Text）からカタログアイテムを引く（アイテム名コンボボックス用, 大小無視）。 */
  findByText(text: string): RateEntry | undefined {
    const t = text.trim().toLowerCase();
    if (!t) return undefined;
    for (const e of this.map.values()) {
      if ((e.text || e.id).trim().toLowerCase() === t) return e;
    }
    return undefined;
  }

  /** 表示単位／手動価格の denomination の選択肢（オーブ3種, §4.1）。取得できたものだけ。 */
  displayUnits(): RateEntry[] {
    const out: RateEntry[] = [];
    for (const id of DISPLAY_UNITS) {
      const e = this.map.get(id);
      if (e && e.rate > 0) out.push(e);
    }
    return out;
  }

  /** market カタログをカテゴリ別にグループ化（optgroup 用, カテゴリ順・各内 rate 降順）。 */
  catalogGroups(): Array<{ label: string; entries: RateEntry[] }> {
    const byCat = new Map<string, RateEntry[]>();
    for (const e of this.map.values()) {
      const key = e.category ?? 'other';
      if (!byCat.has(key)) byCat.set(key, []);
      byCat.get(key)!.push(e);
    }
    const groups: Array<{ label: string; entries: RateEntry[] }> = [];
    for (const cat of MARKET_CATEGORIES) {
      const entries = byCat.get(cat.id);
      if (entries && entries.length) groups.push({ label: cat.label, entries: entries.sort((a, b) => b.rate - a.rate) });
    }
    return groups;
  }
}
